import React, { useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationPatientSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import pacienteService from "../../../services/pacienteService.ts";
import enderecoService from "../../../services/enderecoService.ts";
import { Paciente } from "../../../models/Paciente.ts";
import { Endereco } from "../../../models/Endereco.ts";
import { formatBrazilPhone } from "../../../utils/phone.util.ts";
import { maskCep } from "../../../utils/address.util.ts";
import { maskCpf } from "../../../utils/cpf.util.ts";
import { maskDate, brDateToIso } from "../../../utils/date.util.ts";

interface CreatePacienteDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (r: any) => void;
}

// Mesma receita de cores dos módulos de Hospital/Médico — sem ThemeProvider
// do MUI, estados de label/borda caem no tema claro padrão do MUI, sem
// contraste no fundo escuro. CSS vars do design system em vez de tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const CreatePacienteDrawer: React.FC<CreatePacienteDrawerProps> = ({ open, onClose, onCreated }) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationPatientSchema),
  });

  const handleCepChange = async (value: string) => {
    const masked = maskCep(value);
    setValue("endereco.cep", masked, { shouldValidate: true });
    const raw = masked.replace(/\D/g, "");
    if (raw.length === 8) {
      setBuscandoCep(true);
      await enderecoService.getAddressByZipCode(raw, "endereco", setValue, showFeedback);
      setBuscandoCep(false);
    }
  };

  const handleClose = () => {
    reset({});
    onClose();
  };

  const onSubmit = async (data: any) => {
    const endereco = new Endereco(
      data.endereco?.rua,
      data.endereco?.bairro,
      data.endereco?.cep,
      data.endereco?.numero,
      data.endereco?.cidade,
      data.endereco?.uf
    );
    // id vai 0 aqui só porque o construtor exige um valor — o service de
    // criação remove o campo id do payload antes de enviar (ver pacienteService.create).
    const dataNascimentoIso = data.dataNascimento ? brDateToIso(data.dataNascimento) : "";
    // CPF vazio precisa virar null, não "" — o @CPF do Hibernate Validator no
    // backend aceita null (campo opcional) mas rejeita string vazia como CPF inválido.
    const novoPaciente = new Paciente(0, data.nome, data.email, data.telefone, data.cpf || null, endereco, dataNascimentoIso);

    try {
      setIsLoading(true);
      const resp = await pacienteService.create(novoPaciente);
      showFeedback(resp.message, "success");
      onCreated?.(resp);
      reset({});
    } catch (err: any) {
      showFeedback(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <div>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Novo Paciente</Typography>
          <Typography variant="body2" sx={{ color: "var(--ink-faint)", mt: 0.25 }}>Cadastre um novo paciente</Typography>
        </div>
        <IconButton onClick={handleClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="create-paciente-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do Paciente*" size="small" fullWidth sx={fieldSx}
              {...register("nome")} autoComplete="off" error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="CPF" size="small" fullWidth sx={fieldSx}
              value={watch("cpf") || ""}
              onChange={(e) => setValue("cpf", maskCpf(e.target.value), { shouldValidate: true })}
              error={!!errors.cpf} helperText={errors.cpf?.message as string}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Data de nascimento" placeholder="dd/mm/aaaa" size="small" fullWidth sx={fieldSx}
              value={watch("dataNascimento") || ""}
              onChange={(e) => setValue("dataNascimento", maskDate(e.target.value), { shouldValidate: true })}
              error={!!errors.dataNascimento} helperText={errors.dataNascimento?.message as string}
              InputProps={{
                endAdornment: <InputAdornment position="end"><CalendarTodayOutlinedIcon fontSize="small" sx={{ color: "var(--ink-faint)" }} /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Telefone" size="small" fullWidth sx={fieldSx}
              value={watch("telefone") || ""}
              onChange={(e) => setValue("telefone", formatBrazilPhone(e.target.value), { shouldValidate: true })}
              error={!!errors.telefone} helperText={errors.telefone?.message as string}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="E-mail" size="small" fullWidth sx={fieldSx}
              {...register("email")} error={!!errors.email} helperText={errors.email?.message as string}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--ink)" }}>
              Endereço
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="CEP" size="small" fullWidth sx={fieldSx}
              value={watch("endereco.cep") || ""}
              onChange={(e) => handleCepChange(e.target.value)}
              disabled={buscandoCep}
              error={!!(errors.endereco as any)?.cep}
              helperText={(errors.endereco as any)?.cep?.message}
              InputProps={{ endAdornment: buscandoCep ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : undefined }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Bairro" size="small" fullWidth sx={fieldSx} {...register("endereco.bairro")} />
          </Grid>

          <Grid item xs={6}>
            <TextField label="Cidade" size="small" fullWidth sx={fieldSx} {...register("endereco.cidade")} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="UF" size="small" fullWidth sx={fieldSx} {...register("endereco.uf")}
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
            />
          </Grid>

          <Grid item xs={8}>
            <TextField label="Rua" size="small" fullWidth sx={fieldSx} {...register("endereco.rua")} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Número" size="small" fullWidth sx={fieldSx} {...register("endereco.numero")} />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={handleClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          Cancelar
        </MuiButton>
        <MuiButton type="submit" form="create-paciente-form" variant="contained" disabled={isLoading} disableElevation>
          {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cadastrar"}
        </MuiButton>
      </Box>
    </Drawer>
  );
};

export default CreatePacienteDrawer;
